import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Typography } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import aiInterviewService from "../../../../Services/ai-interview.service";
import useAiAssessmentEvaluate from "../../hooks/useAiAssessmentEvaluate";
import "./assessmentTimer.scss";

type TimerState = "normal" | "warning" | "critical" | "overtime";

const AssessmentTimer: React.FC = () => {
  const { currentSession, assessmentStartTime, isRoundFinished, isEvaluating } = useAIAssessmentContext();
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const timeLimitFiredRef = useRef(false);

  const { handleEvaluation } = useAiAssessmentEvaluate();
  const handleEvaluationRef = useRef(handleEvaluation);
  handleEvaluationRef.current = handleEvaluation;

  const totalTimeInSeconds = useMemo(() => {
    return (currentSession?.estimatedTime || 0) * 60;
  }, [currentSession?.estimatedTime]);

  const calculateRemainingTime = useCallback(() => {
    if (!assessmentStartTime || !totalTimeInSeconds) return null;
    
    const elapsedSeconds = Math.floor((Date.now() - assessmentStartTime) / 1000);
    return totalTimeInSeconds - elapsedSeconds;
  }, [assessmentStartTime, totalTimeInSeconds]);

  useEffect(() => {
    if (!assessmentStartTime || !totalTimeInSeconds || isRoundFinished || isEvaluating) return;

    setRemainingSeconds(calculateRemainingTime());

    const intervalId = setInterval(() => {
      setRemainingSeconds(calculateRemainingTime());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [assessmentStartTime, totalTimeInSeconds, calculateRemainingTime, isRoundFinished, isEvaluating]);

  useEffect(() => {
    if (
      remainingSeconds !== null &&
      remainingSeconds <= 0 &&
      !timeLimitFiredRef.current &&
      !isRoundFinished &&
      currentSession
    ) {
      timeLimitFiredRef.current = true;

      if (!Array.isArray(currentSession.activityLogs)) {
        currentSession.activityLogs = [];
      }
      currentSession.activityLogs.push({
        event: "time_limit_reached",
        timestamp: new Date(),
      });

      aiInterviewService
        .addActivityLogs(currentSession._id, currentSession)
        .catch((error) => {
          console.error("[AssessmentTimer] Failed to log time_limit_reached:", error);
        })
        .finally(() => {
          handleEvaluationRef.current("system");
        });
    }
  }, [remainingSeconds, isRoundFinished, currentSession]);

  const getTimerState = useCallback((): TimerState => {
    if (remainingSeconds === null) return "normal";
    
    if (remainingSeconds < 0) return "overtime";
    
    const percentageRemaining = (remainingSeconds / totalTimeInSeconds) * 100;
    
    if (percentageRemaining <= 25) return "critical";
    if (percentageRemaining <= 50) return "warning";
    return "normal";
  }, [remainingSeconds, totalTimeInSeconds]);

  const formatTime = useCallback((seconds: number | null): string => {
    if (seconds === null) return "--:--";
    
    const isNegative = seconds < 0;
    const absoluteSeconds = Math.abs(seconds);
    
    const minutes = Math.floor(absoluteSeconds / 60);
    const secs = absoluteSeconds % 60;
    
    const formattedTime = `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    
    return isNegative ? `-${formattedTime}` : formattedTime;
  }, []);

  const timerState = getTimerState();
  const formattedTime = formatTime(remainingSeconds);

  if (!assessmentStartTime || !totalTimeInSeconds || isRoundFinished || isEvaluating) {
    return null;
  }

  return (
    <div className={`assessment-timer assessment-timer--${timerState}`}>
      <ClockCircleOutlined className="assessment-timer__icon" />
      <Typography.Text className="assessment-timer__time">
        {formattedTime}
      </Typography.Text>
    </div>
  );
};

export default AssessmentTimer;
